import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil, Calendar as CalendarIcon, Clock, User, Repeat } from "lucide-react";
import TimePicker from "../components/TimePicker";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TYPE_COLORS = {
  follow_up: "bg-[hsl(var(--brand-sage))]/10 text-[hsl(var(--brand-sage))] border-[hsl(var(--brand-sage))]/30",
  consultation: "bg-[hsl(var(--brand-terracotta))]/10 text-[hsl(var(--brand-terracotta))] border-[hsl(var(--brand-terracotta))]/30",
  custom: "bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-border))]",
};
const TYPE_LABELS = { follow_up: "Follow-up", consultation: "Consultation", custom: "Event" };

function startOfWeek(d) {
  const x = new Date(d);
  const day = x.getDay(); // 0 Sun - 6 Sat
  const diff = (day === 0 ? -6 : 1 - day); // shift to Monday
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function toISODate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function EventModal({ open, onClose, onSaved, clients, initial }) {
  const [d, setD] = useState(initial || null);
  const [scope, setScope] = useState("occurrence"); // occurrence | series (only relevant when virtual)
  useEffect(() => {
    setD(initial || null);
    setScope("occurrence");
  }, [initial]);
  if (!open || !d) return null;
  const isVirtual = typeof d.id === "string" && d.id.includes("__");
  const set = (k, v) => setD({ ...d, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        client_id: d.client_id || null,
        title: d.title,
        type: d.type,
        date: d.date,
        time: d.time || "",
        notes: d.notes || "",
        recurring_weekly: !!d.recurring_weekly,
      };
      if (d.id) {
        const url = isVirtual ? `/admin/events/${d.id}?scope=${scope}` : `/admin/events/${d.id}`;
        await api.put(url, payload);
        toast.success(
          isVirtual && scope === "occurrence" ? "This occurrence updated" : isVirtual ? "Series updated" : "Event updated"
        );
      } else {
        await api.post("/admin/events", payload);
        toast.success(payload.recurring_weekly ? "Weekly series scheduled" : "Event added");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };
  const del = async () => {
    if (!d.id) return;
    if (isVirtual) {
      const msg =
        scope === "occurrence"
          ? "Skip just this occurrence? The rest of the weekly series stays."
          : "Delete the entire recurring series?";
      if (!window.confirm(msg)) return;
      await api.delete(`/admin/events/${d.id}?scope=${scope}`);
      toast.success(scope === "occurrence" ? "Occurrence skipped" : "Series deleted");
    } else {
      const msg = d.recurring_weekly ? "Delete the entire recurring series?" : "Delete this event?";
      if (!window.confirm(msg)) return;
      await api.delete(`/admin/events/${d.id}`);
      toast.success("Deleted");
    }
    onSaved();
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[hsl(var(--brand-ink))]/40 p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-7 border border-[hsl(var(--brand-border))] shadow-xl" data-testid="event-form">
        <div className="font-display text-3xl mb-1">{d.id ? (isVirtual ? "Edit occurrence" : "Edit event") : "New event"}</div>
        {isVirtual ? (
          <div className="mb-4">
            <div className="text-xs text-[hsl(var(--brand-muted))] mb-2">Part of a weekly series. Choose what your changes affect:</div>
            <div className="flex gap-2 text-xs" role="radiogroup" aria-label="Edit scope">
              <button
                type="button"
                onClick={() => setScope("occurrence")}
                className={`flex-1 px-3 py-2 rounded-full border ${scope === "occurrence" ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-border))]"}`}
                data-testid="event-scope-occurrence"
              >
                This occurrence
              </button>
              <button
                type="button"
                onClick={() => setScope("series")}
                className={`flex-1 px-3 py-2 rounded-full border ${scope === "series" ? "bg-[hsl(var(--brand-sage))] text-white border-[hsl(var(--brand-sage))]" : "bg-white text-[hsl(var(--brand-ink))] border-[hsl(var(--brand-border))]"}`}
                data-testid="event-scope-series"
              >
                Entire series
              </button>
            </div>
          </div>
        ) : <div className="mb-4" />}
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Type</label>
        <select value={d.type} onChange={(e) => set("type", e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md bg-white mb-3" data-testid="event-type">
          <option value="follow_up">Follow-up</option>
          <option value="consultation">Consultation call</option>
          <option value="custom">Custom</option>
        </select>
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Title</label>
        <input required value={d.title} onChange={(e) => set("title", e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md mb-3" data-testid="event-title" />
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Client (optional)</label>
        <select value={d.client_id || ""} onChange={(e) => set("client_id", e.target.value || null)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md bg-white mb-3" data-testid="event-client">
          <option value="">— No client —</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Date</label>
            <input type="date" required value={d.date} onChange={(e) => set("date", e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="event-date" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Time</label>
            <TimePicker value={d.time || ""} onChange={(v) => set("time", v)} testId="event-time" />
          </div>
        </div>
        {!isVirtual ? (
          <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={!!d.recurring_weekly} onChange={(e) => set("recurring_weekly", e.target.checked)} data-testid="event-recurring" />
            <span>Repeat weekly on the same day</span>
          </label>
        ) : null}
        <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mt-4 mb-1">Notes</label>
        <textarea rows={3} value={d.notes || ""} onChange={(e) => set("notes", e.target.value)} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="event-notes" />
        <div className="flex gap-2 justify-end mt-5">
          {d.id ? (
            <button type="button" onClick={del} className="mr-auto text-sm text-[hsl(var(--destructive))] hover:underline" data-testid="event-delete-btn">
              {isVirtual && scope === "occurrence" ? "Skip this occurrence" : isVirtual ? "Delete series" : "Delete"}
            </button>
          ) : null}
          <button type="button" onClick={onClose} className="px-5 py-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]">Cancel</button>
          <button type="submit" className="px-5 py-2 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="event-save-btn">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminCalendar() {
  const today = new Date();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(today));
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null);

  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const load = async () => {
    const [{ data }, { data: cs }] = await Promise.all([
      api.get(`/admin/calendar?start=${toISODate(weekStart)}&end=${toISODate(weekEnd)}`),
      api.get("/admin/clients"),
    ]);
    setEvents(data.events);
    setClients(cs);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [weekStart]);

  const byDay = useMemo(() => {
    const m = {};
    for (let i = 0; i < 7; i++) m[toISODate(addDays(weekStart, i))] = [];
    for (const e of events) {
      if (m[e.date]) m[e.date].push(e);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }
    return m;
  }, [events, weekStart]);

  const label = `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${weekEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))]">Schedule</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2 flex items-center gap-3">
            <CalendarIcon className="w-8 h-8 text-[hsl(var(--brand-sage))]" /> Your week
          </h1>
          <p className="text-[hsl(var(--brand-muted))] mt-2">Follow-ups, consultation calls, and anything else.</p>
        </div>
        <button
          onClick={() => setModal({ type: "follow_up", title: "", date: toISODate(new Date()), time: "", client_id: "", notes: "", recurring_weekly: false })}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]"
          data-testid="calendar-add-btn"
        >
          <Plus className="w-4 h-4" /> Add event
        </button>
      </div>

      <div className="mt-8 flex items-center justify-between bg-white border border-[hsl(var(--brand-border))] rounded-xl px-5 py-3">
        <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]" data-testid="calendar-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-4">
          <div className="font-display text-xl">{label}</div>
          <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="text-xs text-[hsl(var(--brand-sage))] hover:underline" data-testid="calendar-today">
            Today
          </button>
        </div>
        <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="p-2 rounded-full hover:bg-[hsl(var(--brand-cream-deep))]" data-testid="calendar-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => {
          const d = addDays(weekStart, i);
          const iso = toISODate(d);
          const items = byDay[iso] || [];
          const isToday = toISODate(new Date()) === iso;
          return (
            <div key={iso} className={`rounded-xl border bg-white p-4 min-h-[180px] ${isToday ? "border-[hsl(var(--brand-sage))]" : "border-[hsl(var(--brand-border))]"}`} data-testid={`calendar-day-${iso}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--brand-muted))]">{DAYS[i]}</div>
                  <div className="font-display text-2xl">{d.getDate()}</div>
                </div>
                <button
                  onClick={() => setModal({ type: "follow_up", title: "", date: iso, time: "", client_id: "", notes: "", recurring_weekly: false })}
                  className="p-1.5 rounded-full hover:bg-[hsl(var(--brand-cream-deep))] text-[hsl(var(--brand-muted))]"
                  data-testid={`calendar-quick-add-${iso}`}
                  title="Add event"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <div className="mt-3 space-y-2">
                {items.length === 0 ? (
                  <div className="font-serif-italic text-xs text-[hsl(var(--brand-muted))]">—</div>
                ) : items.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setModal({ ...e })}
                    className={`w-full text-left rounded-md px-3 py-2 border text-xs ${TYPE_COLORS[e.type] || TYPE_COLORS.custom} hover:shadow-sm transition-all`}
                    data-testid={`calendar-event-${e.id}`}
                  >
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-75">
                      {e.time ? <Clock className="w-3 h-3" /> : null}
                      {e.time || TYPE_LABELS[e.type]}
                      {e.recurring_weekly ? <Repeat className="w-3 h-3 ml-auto" title="Weekly" /> : null}
                    </div>
                    <div className="font-medium mt-0.5">{e.title}</div>
                    {e.client ? (
                      <div className="flex items-center gap-1 text-[11px] mt-0.5 opacity-80">
                        <User className="w-3 h-3" /> {e.client.name}
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <EventModal
        open={!!modal}
        onClose={() => setModal(null)}
        clients={clients}
        initial={modal}
        onSaved={load}
      />
    </div>
  );
}
